import React, {useEffect, useState} from 'react';
import AdminMenu from "./AdminMenu";
import AdminHeader from "./AdminHeader";
import axios from "axios";
import Button from "../../components/elements/Button";
import InputField from "../../components/elements/InputField";
import {ADMIN_API_BASE} from "../../utils/apiConfig";
import {faAdd, faSave, faTrashCan} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import CustomSelect from "../../components/elements/CustomSelect";

const AdminCategories = () => {

    const [categories, setCategories] = useState([]);
    const [languagesOptions, setLanguagesOptions] = useState([]);
    const [newCategoryId, setNewCategoryId] = useState("");
    const [error, setError] = useState('');

    useEffect(() => {
        fetchLanguages();
        fetchCategories();
    }, []);

    const getCsrfToken = async () => {
        const response = await fetch(`${ADMIN_API_BASE}/csrf-token`, {
            method: "GET",
            credentials: "include", // critical to send the session cookie
        });

        const data = await response.json();
        return data.csrfToken;
    };

    // -----------------------------
    // Fetch categories
    // -----------------------------
    const fetchCategories = async () => {
        try {
            const token = await getCsrfToken();
            const res = await axios.get(`${ADMIN_API_BASE}/categories`, {
                headers: {'csrf-token': token},
                withCredentials: true
            });

            if (res.data.success) {
                setCategories(res.data.categories.categories);
            } else {
                setError(res.data.error);
            }
        } catch (e) {
            setError(e);
        }
    };

    // -----------------------------
    // Fetch languages
    // -----------------------------
    const fetchLanguages = async () => {
        try {
            const token = await getCsrfToken();
            const res = await axios.get(`${ADMIN_API_BASE}/language`,
                {
                    headers: {'csrf-token': token},
                    withCredentials: true
                }
            );

            if (res.data.success) {
                const options = res.data.languages.languages.map(l => l.id);


                setLanguagesOptions(options);
            } else {
                setError(res.data.error);
            }
        } catch (e) {
            setError(e);
        }
    };

    // -----------------------------
    // Create category
    // -----------------------------
    const createCategory = async () => {

        if (!newCategoryId) return;

        const token = await getCsrfToken();
        await axios.post(`${ADMIN_API_BASE}/categories`, {
                id: newCategoryId
            },
            {
                headers: {'csrf-token': token},
                withCredentials: true
            });

        setNewCategoryId("");
        fetchCategories();
    };

    // -----------------------------
    // Delete category
    // -----------------------------
    const deleteCategory = async (id) => {
        const token = await getCsrfToken();
        await axios.delete(`${ADMIN_API_BASE}/categories/${id}`,
            {
                headers: {'csrf-token': token},
                withCredentials: true
            });
        fetchCategories();
    };

    // -----------------------------
    // Delete translation row
    // -----------------------------
    const deleteTranslation = async (catid, langid) => {
        const token = await getCsrfToken();
        await axios.delete(`${ADMIN_API_BASE}/categories/translation`, {
                category_id: catid,
                language_id: langid
            },
            {
                headers: {'csrf-token': token},
                withCredentials: true
            });

        fetchCategories();
    };


    // -----------------------------
    // Add translation row
    // -----------------------------
    const addTranslation = (index) => {

        const updated = [...categories];

        const usedLanguages = updated[index].translations.map(t => t.language_id);

        const availableLanguages = languagesOptions
            .map(l => l.value ?? l)
            .filter(lang => !usedLanguages.includes(lang));

        if (availableLanguages.length === 0) return;

        updated[index].translations.push({
            language_id: availableLanguages[0],
            category: "",
            type: "new"
        });

        setCategories(updated);
    };


    // -----------------------------
    // Translation change
    // -----------------------------
    const handleTranslationChange = (catIndex, transIndex, name, value) => {

        const updated = [...categories];

        updated[catIndex].translations[transIndex][name] = value;

        setCategories(updated);
    };

    // -----------------------------
    // Save translation
    // -----------------------------
    const saveTranslation = async (category_id, translation) => {

        if (!translation.language_id || !translation.category) {
            alert("Language and category name are required");
            return;
        }

        const token = await getCsrfToken();

        if (translation.type === "new") {

            await axios.post(`${ADMIN_API_BASE}/categories/translation`, {
                    category_id: category_id,
                    language_id: translation.language_id,
                    category: translation.category
                },
                {
                    headers: {'csrf-token': token},
                    withCredentials: true
                });

        } else {

            await axios.put(`${ADMIN_API_BASE}/categories`, {
                    category_id: category_id,
                    language_id: translation.language_id,
                    category: translation.category
                },
                {
                    headers: {'csrf-token': token},
                    withCredentials: true
                });

        }

        fetchCategories();
    };

    return (
        <div className="flex flex-1">

            <div className='w-1/5'>
                <AdminMenu path='./'/>
            </div>

            <div className='flex-grow w-4/5'>
                <AdminHeader title='دسته‌بندی'/>

                <div className='w-full px-10 my-10'>

                    {/* Create category */}
                    <div className="flex w-full items-center gap-3 mb-10">

                        <InputField
                            value={newCategoryId}
                            title={'Category ID'}
                            name="id"
                            onChange={(e) => setNewCategoryId(e.target.value)}
                        />

                        <Button onClick={createCategory} className='h-fit'>
                            <FontAwesomeIcon icon={faAdd}/>
                        </Button>

                    </div>

                    {/* Categories */}
                    <div className="flex full flex-col gap-10">

                        {categories.map((cat, catIndex) => (

                            <div key={cat.id} className="bg-gray-200 p-5 rounded-lg">

                                {/* Category Header */}
                                <div className="flex items-center gap-4 mb-5">

                                    <div className="font-bold text-lg">
                                        {cat.id}
                                    </div>

                                    <Button onClick={() => deleteCategory(cat.id)}>
                                        <FontAwesomeIcon icon={faTrashCan}/>
                                    </Button>

                                    <Button onClick={() => addTranslation(catIndex)}>
                                        <FontAwesomeIcon icon={faAdd}/>
                                        <span>ترجمه</span>
                                    </Button>

                                </div>

                                {/* Translations */}
                                <div className="flex flex-col gap-3">

                                    {cat.translations?.map((t, transIndex) => (

                                        <div key={transIndex} className="flex gap-3 items-center">

                                            <CustomSelect
                                                value={t.language_id}
                                                options={languagesOptions}
                                                title="زبان"
                                                onChange={(e) =>
                                                    handleTranslationChange(
                                                        catIndex,
                                                        transIndex,
                                                        "language_id",
                                                        e.target.value
                                                    )
                                                }
                                            />

                                            <InputField
                                                value={t.category}
                                                title="Category Name"
                                                onChange={(e) =>
                                                    handleTranslationChange(
                                                        catIndex,
                                                        transIndex,
                                                        "category",
                                                        e.target.value
                                                    )
                                                }
                                            />

                                            <Button
                                                onClick={() =>
                                                    saveTranslation(cat.id, t)
                                                }>
                                                <FontAwesomeIcon icon={faSave}/>
                                            </Button>

                                            <Button
                                                onClick={() => deleteTranslation(cat.id, t.language_id)}
                                            >
                                                <FontAwesomeIcon icon={faTrashCan}/>
                                            </Button>

                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminCategories;
